import { IUser } from '@my-backoffice/app/pages/user/shared/models/interfaces/user.interface';
import { ECookieKeys } from '@my-backoffice/app/shared/models/enums/cookie-keys.enum';
import { AppLocationService } from '@my-backoffice/app/shared/services/browser-services/app-location/app-location-service';
import { CookieStorageService } from '@my-backoffice/app/shared/services/storage-services/cookie-storage/cookie-storage.service';
import { AppUserSettingService } from '@my-backoffice/app/shared/services/user-services/app-user-setting/app-user-setting.service';
import { LOGROCKET_ENVIRONMENT_ID } from '@my-backoffice/app/shared/tokens/logrocket-enviroment-id.token';
import packageJson from '@my-backoffice/root-package.json';
import { Inject, Injectable } from '@angular/core';
import { isEmpty } from 'lodash/index';
// eslint-disable-next-line @typescript-eslint/naming-convention
import LogRocket from 'logrocket';

interface IRequest {
  body?: string;
  credentials?: string;
  headers: { [key: string]: string | undefined };
  method: string;
  mode?: string;
  referrer?: string;
  reqId: string;
  url: string;
}

interface IResponse {
  body?: string;
  headers: { [key: string]: string | undefined };
  method: string;
  reqId: string;
  status?: number;
  url: string;
}

// Todo: replace the urls with aca
export const ENDPOINTS_WITH_PASSWORD: string[] = [
  'api/authenticate', // Login page
  'api/account/set-password', // Forgot password page
  'api/account/change-password', // Change password page
  'api/invitation/inspector/', // Inspectors invitation confirmation page
  'api/factories/invite', // Factories invitation confirmation page
  'api/invitation/auditor/', // Auditors invitation confirmation page
];

@Injectable({
  providedIn: 'root',
})
export class LogrocketInitService {
  public constructor(
    @Inject(LOGROCKET_ENVIRONMENT_ID) private readonly _logrocketEnvironmentId: Readonly<string>,
    private readonly _appLocationService: AppLocationService,
    private readonly _appUserSettingService: AppUserSettingService,
    private readonly _cookieStorage: CookieStorageService
  ) {}

  public init(): void {
    const token = this._cookieStorage.get(ECookieKeys.AUTH_TOKEN);
    const sessionId = this._cookieStorage.get(ECookieKeys.SESSION_ID);
    const isTestCompany = this._appUserSettingService.getIsTestCompany();

    if (!token || !sessionId || isTestCompany || !this._logrocketEnvironmentId || isEmpty(this._logrocketEnvironmentId.trim())) {
      return;
    }

    LogRocket.init(this._logrocketEnvironmentId, {
      console: {
        shouldAggregateConsoleErrors: true,
      },
      network: {
        requestSanitizer: this.requestSanitizerLogRocket,
        responseSanitizer: this.responseSanitizerLogRocket,
      },
      mergeIframes: true,
      childDomains: [this._appLocationService.getAcaUrl()],
    });
  }

  public requestSanitizerLogRocket(request: IRequest): IRequest {
    if (ENDPOINTS_WITH_PASSWORD.some((path: string): boolean => request?.url?.toLowerCase().includes(path))) {
      request.body = undefined;
    }

    request.headers['Authorization'] = undefined;
    request.headers['Cookie'] = undefined;

    return request;
  }

  public responseSanitizerLogRocket(response: IResponse): IResponse {
    if (ENDPOINTS_WITH_PASSWORD.some((path: string): boolean => response.url.toLowerCase().includes(path))) {
      response.body = undefined;
    }

    response.headers['authorization'] = undefined;

    return response;
  }

  public identifyUserForLogRocket(userData: Readonly<IUser>): void {
    const userId = userData.id || (userData as IUser & { userId?: string }).userId || userData.login || '';

    LogRocket.identify(userId, {
      name: userData.login,
      id: userId,
      email: userData.email,
      company: userData.company?.name,
      companyId: userData.company?.id,
    });
    LogRocket.track('application', { version: packageJson.version });
  }
}
